<template>
  <layout>
    <!-- @todo This should probably be a `header` slot which can be used for every layout -->
    <div
      class="space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.categories.title") }}
        </h1>
        <template v-if="hasPermission(permissions.createCategory)">
          <primary-button
            class="hidden sm:flex"
            :label="t('admin.settings.categories.add_category')"
            @click="openCategorySlideOver = true"
          />
          <PlusIcon
            class="w-10 text-gray-900 sm:hidden -mr-1"
            @click="openCategorySlideOver = true"
          />
        </template>
      </div>
    </div>
    <div class="flex flex-col dynamic-height overflow-auto">
      <div
        v-for="category in props.categories"
        :key="category.id"
        class="px-6 py-5 font-medium bg-white border-b border-b-gray-200 flex items-center space-x-4 cursor-pointer hover:bg-gray-50"
        @click="handleCategoryClicked(category)"
      >
        <span
          class="material-symbols-outlined"
          :class="getTailwindColor(category.color)"
          >{{ category.icon }}</span
        >
        <span class="text-sm">{{ category.name }}</span>
      </div>
    </div>

    <settings-panel
      :open="openCategorySlideOver"
      :details="parsedCategoryDetails"
      :fields="categoryFields"
      :canCreate="hasPermission(permissions.createCategory)"
      :canUpdate="hasPermission(permissions.updateCategory)"
      :canDelete="hasPermission(permissions.deleteCategory)"
      :createLabel="t('admin.settings.categories.add_category')"
      :updateLabel="t('admin.settings.categories.update_category')"
      :is-create-loading="isCreateLoading"
      :is-update-loading="isUpdateLoading"
      :is-delete-loading="isDeleteLoading"
      @close="handleCloseIconSelector"
      @create="handleCreateCategory"
      @update="handleUpdateCategory"
      @delete="handleDeleteCategory"
    />
  </layout>
</template>

<script setup>
import PersistentLayout from "@/Layouts/PersistentLayout.vue";
import Layout from "@/Tailwind/Layouts/TenantSettings";
import { computed, defineProps, inject, reactive, ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { usePage } from "@inertiajs/vue3";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import SettingsPanel from "@/Tailwind/Components/Settings/SettingsPanel.vue";
import IconSelector from "@/Tailwind/Components/Settings/Categories/IconSelector.vue";
import Translations from "@/Tailwind/Components/Settings/Categories/Translations.vue";

import { useI18n } from "vue-i18n";
import { usePreloadStore } from "@/Tailwind/Stores/preload";
const { t } = useI18n();

defineOptions({ layout: PersistentLayout });

const props = defineProps({
  category: Object,
  categories: Array,
});

const preloadStore = usePreloadStore();

const isCreateLoading = ref(false);
const isUpdateLoading = ref(false);
const isDeleteLoading = ref(false);

const { hasPermission, permissions } = inject("permissions");

const tenant = usePage().props.tenant;
const locale = usePage().props.locale;
const openCategorySlideOver = ref(!!props.category);

const handleCloseIconSelector = () =>
  router.visit(route("settings.categories.index", [tenant]));

const handleCategoryClicked = (category) =>
  router.visit(route("settings.categories.edit", [tenant, category]));

const getTailwindColor = (color) => ({
  "text-pink-500": color === "#F3407F",
  "text-yellow-500": color === "#864817",
  "text-gray-500": color === "#676C75",
  "text-amber-500": color === "#ffa000",
  "text-zinc-500": color === "#2C2C2C",
  "text-cyan-500": color === "#00bcd4",
  "text-sky-500": color === "#2196f3",
  "text-slate-500": color === "#607d8b",
  "text-stone-500": color === "#795546",
  "text-fuchsia-500": color === "#9c27b0",
  "text-orange-500": color === "#ff9800",
  "text-blue-500": color === "#2368ff",
  "text-green-500": color === "#2bbd38",
});

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}

const defaultIconColor = usePage().props.defaultColor;

// Category management
const categoryFields = computed(() => [
  {
    key: "icon",
    title: t("admin.settings.categories.category_icon.title"),
    description: t("admin.settings.categories.category_icon.description"),
    component: IconSelector,
    extraAttributes: {
      defaultColor: defaultIconColor,
    },
  },
  {
    key: "name",
    title: t("admin.settings.categories.name.title"),
    description: t("admin.settings.categories.name.description"),
    placeholder: t("admin.settings.categories.name.placeholder"),
    type: "text",
  },
  {
    key: "detectable",
    title: t("admin.settings.categories.detectable.title"),
    description: t("admin.settings.categories.detectable.description"),
    type: "switch",
    default: true,
  },
  {
    key: "translations",
    title: t("admin.settings.categories.translations.title"),
    component: Translations,
    show: () => !!props.category?.id,
  },
]);

const translations = reactive({
  en: props.category?.name_english,
  nl: props.category?.name_dutch,
  fr: props.category?.name_french,
  de: props.category?.name_german,
  es: props.category?.name_spanish,
  bg: props.category?.name_bulgarian,
  zh: props.category?.name_chinese,
  cs: props.category?.name_czech,
  da: props.category?.name_danish,
  et: props.category?.name_estonian,
  fi: props.category?.name_finnish,
  el: props.category?.name_greek,
  hu: props.category?.name_hungarian,
  id: props.category?.name_indonesian,
  it: props.category?.name_italian,
  ja: props.category?.name_japanese,
  lv: props.category?.name_latvian,
  lt: props.category?.name_lithuanian,
  pl: props.category?.name_polish,
  ro: props.category?.name_romanian,
  ru: props.category?.name_russian,
  sk: props.category?.name_slovak,
  sl: props.category?.name_slovenian,
  sv: props.category?.name_swedish,
  tr: props.category?.name_turkish,
  pt: props.category?.name_portuguese,
});

const parsedCategoryDetails = computed(() => {
  if (!props.category) {
    return {
      icon: {},
    };
  }

  return {
    ...props.category,
    icon: {
      icon: props.category.icon,
      color: props.category.color,
    },
    name: props.category ? translations[locale] : null,
    translations,
  };
});

function handleCreateCategory(inertiaForm) {
  inertiaForm
    .transform((data) => ({
      ...data,
      ...data.icon,
      name: {
        [locale]: data.name,
      },
    }))
    .post(route("settings.categories.store", [tenant]), {
      onStart: () => {
        preloadStore.loadCategories(true);
        isCreateLoading.value = true;
      },
      onSuccess: () => {
        handleCategoryPanelClose();
      },
      onError: () => (isCreateLoading.value = false),
    });
}

function handleUpdateCategory(inertiaForm) {
  inertiaForm
    .transform((data) => ({
      ...data,
      ...data.icon,
      name: {
        ...data.translations,
        [locale]: data.name,
      },
    }))
    .put(route("settings.categories.update", [tenant, props.category]), {
      onStart: () => (isUpdateLoading.value = true),
      onSuccess: () => {
        preloadStore.loadCategories(true);
        handleCategoryPanelClose();
      },
      onError: () => (isUpdateLoading.value = false),
    });
}

function handleDeleteCategory() {
  if (!hasPermission(permissions.deleteCategory)) {
    return;
  }

  router.delete(
    route("settings.categories.destroy", [tenant, props.category]),
    {
      onStart: () => (isDeleteLoading.value = true),
      onSuccess: () => {
        preloadStore.loadCategories(true);
        handleCategoryPanelClose();
      },
      onError: () => (isDeleteLoading.value = false),
    }
  );
}

function handleCategoryPanelClose() {
  router.visit(route("settings.categories.index", [tenant]));
  openCategorySlideOver.value = false;
}

// -----------------------------
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
